import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useSound } from '@/hooks/useSound';
import { AgButton } from '@/components/ui/agartha';
import { useAgSkin } from '@/components/ui/agartha/skin';

/** Akcent FantasyButton → wariant / kamień pigułki Agartha (gdy aktywna skórka Agartha). */
const AG_FROM_ACCENT = {
  gold: { variant: 'primary' },
  ember: { variant: 'primary' },
  steel: { variant: 'ghost', gem: 'silver' },
  blood: { variant: 'danger' },
  emerald: { variant: 'success' },
  magic: { variant: 'purple' },
  arcane: { variant: 'arcane', gem: 'blue' },
};

export const FANTASY_ACCENTS = {
  gold:    { border: 'var(--gold-primary)', borderDark: 'var(--gold-dark)',  glow: 'var(--gold-glow)',          text: 'var(--gold-light)',  icon: 'var(--gold-primary)',  line: 'var(--gold-dark)',    ornament: 'var(--gold-primary)', bg: 'rgba(158,110,37,0.08)', particle: '#EBC794' },
  blood:   { border: 'var(--blood-red)',    borderDark: '#5c0000',           glow: 'rgba(139,0,0,0.35)',        text: '#fca5a5',           icon: '#ef4444',              line: 'var(--blood-red)',    ornament: '#8B0000',             bg: 'rgba(139,0,0,0.06)',   particle: '#EF4444' },
  magic:   { border: 'var(--magic-purple)', borderDark: '#4a1a55',           glow: 'var(--magic-glow)',         text: '#c4b5fd',           icon: '#a78bfa',              line: 'var(--magic-purple)', ornament: '#7B2D8E',             bg: 'rgba(123,45,142,0.06)', particle: '#A78BFA' },
  ember:   { border: 'var(--fire-orange)',  borderDark: '#8a3700',           glow: 'var(--ember-glow)',         text: '#fcd34d',           icon: '#f59e0b',              line: 'var(--fire-orange)',  ornament: '#D35400',             bg: 'rgba(211,84,0,0.06)',  particle: '#F59E0B' },
  steel:   { border: '#4b5563',            borderDark: '#374151',            glow: 'rgba(107,114,128,0.25)',    text: '#d1d5db',           icon: '#9ca3af',              line: '#4b5563',            ornament: '#6b7280',             bg: 'rgba(107,114,128,0.05)', particle: '#9CA3AF' },
  emerald: { border: '#065f46',            borderDark: '#064e3b',            glow: 'rgba(16,185,129,0.25)',     text: '#6ee7b7',           icon: '#34d399',              line: '#065f46',            ornament: '#059669',             bg: 'rgba(16,185,129,0.05)', particle: '#34D399' },
  arcane:  { border: 'var(--magic-blue)',   borderDark: '#152d4a',           glow: 'rgba(30,58,95,0.4)',        text: '#93c5fd',           icon: '#60a5fa',              line: 'var(--magic-blue)',   ornament: '#2563eb',             bg: 'rgba(30,58,95,0.06)',  particle: '#60A5FA' },
};

/* Small decorative corner piece */
function CornerOrnament({ position, color }) {
  const posClass = {
    'top-left':     'top-0 left-0',
    'top-right':    'top-0 right-0 -scale-x-100',
    'bottom-left':  'bottom-0 left-0 -scale-y-100',
    'bottom-right': 'bottom-0 right-0 -scale-x-100 -scale-y-100',
  }[position];

  return (
    <div className={`absolute ${posClass} pointer-events-none z-20 w-3 h-3 sm:w-3.5 sm:h-3.5`}>
      <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
        <path d="M0 0 L6 0 L4 2 L2 2 L2 4 L0 6 Z" fill={color} fillOpacity="0.7" />
        <path d="M0 0 L6 0 L4 2 L2 2 L2 4 L0 6 Z" stroke={color} strokeWidth="0.5" strokeOpacity="0.4" fill="none" />
      </svg>
    </div>
  );
}

function ButtonParticles({ color }) {
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-lg">
      {[...Array(8)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-[1.5px] h-[1.5px] rounded-full"
          style={{ 
            backgroundColor: color, 
            boxShadow: `0 0 4px ${color}, 0 0 1px white`,
            left: `${10 + Math.random() * 80}%`,
            top: '100%'
          }}
          initial={{ opacity: 0, y: 0 }}
          animate={{
            opacity: [0, 1, 0],
            y: [-10, -40 - Math.random() * 20],
            x: [(Math.random() - 0.5) * 30],
            scale: [0, 1.2, 0]
          }}
          transition={{
            duration: 1.5 + Math.random(),
            repeat: Infinity,
            delay: Math.random() * 1.5,
            ease: "easeOut"
          }}
        />
      ))}
    </div>
  );
}

export function FantasyButton({ onClick, disabled, icon: Icon, children, accent = 'gold', chevron, chevronOpen, className = '', childrenClasses = '' }) {
  const [isHovered, setIsHovered] = useState(false);
  const { animationsEnabled } = useSound();
  const agSkin = useAgSkin();
  if (agSkin) {
    const ag = AG_FROM_ACCENT[accent] || AG_FROM_ACCENT.gold;
    return (
      <AgButton
        variant={ag.variant}
        gem={Icon ? undefined : ag.gem}
        icon={Icon}
        onClick={onClick}
        disabled={!!disabled}
        className={`ag-btn--fantasy ${className}`}
      >
        <span className={`inline-flex items-center gap-2 ${childrenClasses}`}>
          {children}
          {chevron && <ChevronDown className={`w-4 h-4 transition-transform ${chevronOpen ? 'rotate-180' : ''}`} />}
        </span>
      </AgButton>
    );
  }
  const a = FANTASY_ACCENTS[accent];
  const isDisabled = !!disabled;

  return (
    <motion.button
      whileHover={isDisabled ? {} : { y: -1 }}
      whileTap={isDisabled ? {} : { y: 1 }}
      onClick={onClick}
      disabled={isDisabled}
      onMouseEnter={() => !isDisabled && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={`
        cursor-pointer relative w-full flex items-center justify-center gap-2 sm:gap-3
        px-5 sm:px-8 py-3 sm:py-3.5 rounded-lg overflow-visible group/btn
        font-gothic text-sm sm:text-base tracking-wider font-semibold
        disabled:opacity-30 disabled:cursor-not-allowed
        ${className}
      `}
      style={{ color: a.text }}
    >
      <AnimatePresence>
        {isHovered && !isDisabled && animationsEnabled && <ButtonParticles color={a.particle} />}
      </AnimatePresence>

      {/* ── Outer frame (dark ring) ── */}

      <div
        className="absolute inset-0 rounded-lg pointer-events-none"
        style={{
          border: `2px solid ${isDisabled ? 'var(--border-dark)' : a.borderDark}`,
          boxShadow: isDisabled ? 'none' : `
            0 2px 8px rgba(0,0,0,0.6),
            0 0 20px ${a.glow}
          `,
        }}
      />

      {/* ── Inner plate (beveled metal surface) ── */}
      <div
        className="absolute inset-[3px] rounded-[5px] pointer-events-none"
        style={{
          background: `
            linear-gradient(180deg,
              rgba(255,255,255,0.04) 0%,
              ${a.bg} 15%,
              rgba(0,0,0,0.1) 50%,
              rgba(0,0,0,0.25) 100%
            )
          `,
          borderWidth: '1px',
          borderStyle: 'solid',
          borderTopColor: isDisabled ? 'transparent' : `color-mix(in srgb, ${a.border} 60%, white)`,
          borderRightColor: isDisabled ? 'transparent' : a.border,
          borderBottomColor: isDisabled ? 'transparent' : 'rgba(0,0,0,0.4)',
          borderLeftColor: isDisabled ? 'transparent' : a.border,
          boxShadow: isDisabled ? 'none' : `
            inset 0 1px 0 rgba(255,255,255,0.08),
            inset 0 -1px 0 rgba(0,0,0,0.3),
            inset 0 0 20px rgba(0,0,0,0.3)
          `,
        }}
      />

      {/* ── Metal texture (subtle noise grain) ── */}
      <div
        className="absolute inset-[3px] rounded-[5px] pointer-events-none opacity-[0.03] mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
          backgroundSize: '128px 128px',
        }}
      />

      {/* ── Gilded top edge ── */}
      {!isDisabled && (
        <div className="absolute top-[2px] left-3 right-3 h-[1px] pointer-events-none z-10">
          <div className="w-full h-full" style={{
            background: `linear-gradient(to right, transparent, ${a.line}, ${a.border}, ${a.line}, transparent)`,
            opacity: 0.6,
          }} />
        </div>
      )}

      {/* ── Corner ornaments ── */}
      {!isDisabled && (
        <>
          <CornerOrnament position="top-left" color={a.ornament} />
          <CornerOrnament position="top-right" color={a.ornament} />
          <CornerOrnament position="bottom-left" color={a.ornament} />
          <CornerOrnament position="bottom-right" color={a.ornament} />
        </>
      )}

      {/* ── Hover warm glow (embers heating) ── */}
      {!isDisabled && (
        <div
          className="absolute inset-[3px] rounded-[5px] pointer-events-none opacity-0 group-hover/btn:opacity-100 transition-opacity duration-500"
          style={{
            background: `radial-gradient(ellipse at 50% 60%, ${a.glow} 0%, transparent 70%)`,
          }}
        />
      )}

      {/* ── Shimmer (slow forge-light sweep) ── */}
      {!isDisabled && animationsEnabled && (
        <motion.div
          className="absolute inset-[3px] rounded-[5px] pointer-events-none overflow-hidden"
          style={{ opacity: 0.12 }}
        >
          <motion.div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(105deg, transparent 40%, ${a.glow} 50%, transparent 60%)`,
              width: '200%',
            }}
            initial={{ x: '-100%' }}
            animate={{ x: '100%' }}
            transition={{ duration: 4, repeat: Infinity, repeatDelay: 6, ease: 'easeInOut' }}
          />
        </motion.div>
      )}

      {/* ── Content ── */}
      {Icon && <Icon className="w-4 h-4 sm:w-5 sm:h-5 relative z-10 shrink-0 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" style={{ color: a.icon }} />}
      {children && <div className={`relative z-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)] text-nowrap ${childrenClasses}`}>{children}</div>}
      {chevron && (
        <motion.div animate={{ rotate: chevronOpen ? 180 : 0 }} transition={{ duration: 0.3 }} className="relative z-10 shrink-0">
          <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]" style={{ color: a.icon }} />
        </motion.div>
      )}
    </motion.button>
  );
}

export function ActionButton({ onClick, disabled, icon: Icon, children, variant = 'primary', className = '' }) {
  const accentMap = {
    primary: 'gold',
    secondary: 'steel',
    danger: 'blood',
    magic: 'magic',
    amber: 'ember'
  };

  return (
    <FantasyButton
      onClick={onClick}
      disabled={disabled}
      icon={Icon}
      accent={accentMap[variant] || 'gold'}
      className={className}
    >
      {children}
    </FantasyButton>
  );
}
